/// app.component.ts
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, HostBinding, HostListener, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';

import { OAuthService } from 'angular-oauth2-oidc';
import { filter, Subject, takeUntil } from 'rxjs';
import { ToastService } from 'src/app/core/services/toast.service';
import { environment } from 'src/environments/environment';

import { AppUtilityService } from './app-utility.service';
import { AutoLogoutService } from './core/services/auto-logout.service';
import { QuoteChangedService } from './core/services/changed-service.service';
import { UserService } from './core/services/lmsuser.service';
import { ServiceWorkerService } from './core/services/service-worker.service';
import { ThemeService } from './core/services/theme.service';
import { LoadSpinnerService } from './shared/loaders/spinner/loadspinner.service';
import { NotificationService } from './shared/notification/notification.service';
import { SlidePanelService } from './shared/ui/slide-panel';

export interface LoanApplication {
  appId: string;
  applicantName: string;
  idNumber: string;
  contactNumber: string;
  dateApplied: Date;
  amount: number;
  termMonths: number;
  status: 'Approved' | 'Pending Review' | 'Declined';
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  // --- Standard Properties ---
  title = 'CLMS-client';
  environmentName!: string;
  searchId = '';

  // Use correct typing instead of 'any'
  public getScreenWidth: number = window.innerWidth;
  public getScreenHeight: number = window.innerHeight;

  is_loader_showing = false;
  Page_Title = '';
  isPdfLoaded = false;
  zoom = 'auto';
  page = 1;

  pVisible = false;
  panelAvailable = false;

  // --- State Variables ---
  private canNavigate: boolean = true;

  private destroy$ = new Subject<void>();
  public isLoggedIn$ = this.userService.LMSUserLoggedIn$;

  selectedApp: LoanApplication | null = null;
  applications: LoanApplication[] = [ /* ... your mock data ... */ ];

  // --- Getters for HTML Template Cleanliness ---
  get currentYear(): number {
    return new Date().getFullYear();
  }

  get userBranchName(): string {
    return this.userService.userBranchName || 'Default Branch';
  }

  get userJobTitle(): string {
    return this.userService.userJobTitle || 'User';
  }

  isTabletMode: boolean = false;
  @HostBinding('attr.data-theme') appTheme: string = '';

  routelink: ActivatedRoute | null = null;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public global_utility: AppUtilityService,
    private changesService: QuoteChangedService,
    private spinnerService: LoadSpinnerService,
    private panelService: SlidePanelService,
    public autoLogoutService: AutoLogoutService,
    private toastService: ToastService,
    private serviceworker: ServiceWorkerService,
    private notificationService: NotificationService,
    public userService: UserService,
    @Inject(OAuthService) private oauthService: OAuthService,
    @Inject(BreakpointObserver) private breakpointObserver: BreakpointObserver,
    public themeService: ThemeService,
    @Inject(Renderer2) private renderer: Renderer2
  ) { }

  async ngOnInit() {
    this.applyTheme(this.global_utility.initAppTheme());

    this.global_utility.currentThemeSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe((theme: string) => {
        this.applyTheme(theme || '');
      });

    this.environmentName = environment.name;
    await this.serviceworker.checkSWUpdate();

    if ((module as any).hot) {
      (module as any).hot.accept();
      if (this.oauthService.hasValidAccessToken() || this.oauthService.hasValidIdToken()) {
        this.userService.loadLMSUserProfile().catch((err) => console.warn('HMR error', err));
      }
    } else {
      this.global_utility.startLoading('Authenticating..');
      setTimeout(() => this.global_utility.stopLoading(), 500);
    }

    // Automatically apply a helper class for your tablet-dev environment
    if (this.environmentName === 'tablet-dev' || this.environmentName === 'local') {
      // Adding to body allows you to scope all tablet styles easily
      this.renderer.addClass(document.body, 'env-tablet');
      console.log(`[UI Update]: Tablet touch-optimizations enabled for ${this.environmentName}`);
    }

    this.autoLogoutService.check();

    // 1. FIXED BEFOREUNLOAD BUG: Maintain synchronous state for HostListener
    this.changesService.allowNavigation$
      .pipe(takeUntil(this.destroy$))
      .subscribe(canNavigate => {
        this.canNavigate = canNavigate;
      });

    // BEST PRACTICE: If you need to listen to global title changes outside of routing,
    // subscribe to an Observable in global_utility instead of polling.
    // Listen to the Title Subject globally
    this.global_utility.pageTitle$
      .pipe(takeUntil(this.destroy$))
      .subscribe((title: string) => {
        // This runs instantly whenever .next() is called in the service
        this.Page_Title = title;
      });
    // 2. FIXED MEMORY LEAK: Handle Route Titles without setInterval
    // Handle Route-based titles
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd || event instanceof NavigationStart),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if(event instanceof NavigationStart) {
        this.global_utility.isBusyNavigating(true);
      }
      if(event instanceof NavigationEnd) {
        this.global_utility.isBusyNavigating(false);
      }
      const rt = this._getChild(this.activatedRoute);
      this.routelink = rt;


      rt.data.pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
        if (data.title) {
          // Push the new route title into the global stream
          this.global_utility.updatePageTitle(data.title);
        }
      });
    });

    this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.Tablet])
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.isTabletMode = result.matches;
    });
  }

  // Acts as a bridge between the HTML template and your utility service
  onRouteActivation(event: any): void {
    if (this.global_utility && this.global_utility.onRouteActivation) {
      this.global_utility.onRouteActivation(event);
    }
  }


  private _getChild(activatedRoute: ActivatedRoute): ActivatedRoute {
    return activatedRoute.firstChild ? this._getChild(activatedRoute.firstChild) : activatedRoute;
  }

  // Add to AppComponent
  public toggleDarkMode(isDark: boolean) {
    const theme = isDark ? 'dark' : 'light';
    this.global_utility.setAppTheme(theme);
  }

  private applyTheme(theme: string): void {
    this.appTheme = theme;
    this.renderer.setAttribute(document.documentElement, 'data-theme', theme);
    this.renderer.setAttribute(document.body, 'data-theme', theme);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Uses proper typing (BeforeUnloadEvent) and checks the synchronous boolean
  @HostListener('window:beforeunload', ['$event'])
  public onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.canNavigate) {
      event.preventDefault();
      // Chrome requires returnValue to be set
      event.returnValue = false;
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.getScreenWidth = window.innerWidth;
    this.getScreenHeight = window.innerHeight;
  }
}
